import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Store } from '@ngxs/store';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { DropdownModule } from 'primeng/dropdown';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { InputTextModule } from 'primeng/inputtext';
import { RippleModule } from 'primeng/ripple';
import { MessageService } from 'primeng/api';
import { OverlayPanelModule } from 'primeng/overlaypanel';

import { UserSelectors } from '../../../states/user/user-selector';
import { InstituteService } from '../../../services/institute/institute.service';
import { SearchInstituteResponse } from '../../../models/institute/searchInstituteResponse';
import { RoiCalculatorService, RoiCalculationResponse } from '../../../services/roi-calculator/roi-calculator.service';
import { InstituteCoursesV2Response } from '../../../models/institute/instituteCoursesV2Response';
import { MentorService } from '../../../services/account/mentor.service';
import { MentorType } from '../../../models/enums/enums';
import { StudentMentorResponse } from '../../../models/account/mentor/studentMentorResponse';

interface DropdownOption {
  name: string;
  code: string | number;
}

@Component({
  selector: 'app-roi-calculator',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    FormsModule,
    AutoCompleteModule,
    DropdownModule,
    ButtonModule,
    CheckboxModule,
    DialogModule,
    ToastModule,
    InputTextModule,
    RippleModule,
    OverlayPanelModule
  ],
  templateUrl: './roi-calculator.component.html',
  styleUrls: ['./roi-calculator.component.scss'],
  providers: [MessageService]
})
export class RoiCalculatorComponent implements OnInit {
  roiForm!: FormGroup;
  isLoggedIn = false;
  isLoading = false;
  isLoadingCourses = false;
  activeView: 'landing' | 'form' | 'dashboard' = 'landing';
  formDialogVisible = false;
  simplifiedVisible = false;
  simplifiedVariant: 'A' | 'B' = 'A';
  currencySymbol = '$';
  selectedRoiYears = 10;
  
  // Form autocomplete and dropdown options
  universitySearchSuggestions: any[] = [];
  startYears: DropdownOption[] = [
    { name: '2026', code: 2026 },
    { name: '2027', code: 2027 },
    { name: '2028', code: 2028 }
  ];
  levelsList: DropdownOption[] = [
    { name: 'Undergraduate (UG)', code: 'UG' },
    { name: 'Postgraduate (PG)', code: 'PG' }
  ];
  programTypes: DropdownOption[] = []; // Not used, kept for reference

  allCourses: InstituteCoursesV2Response[] = [];
  schoolsList: DropdownOption[] = [];
  coursesList: DropdownOption[] = [];

  // Scholarship Sub-section toggle
  hasScholarship = false;

  // Mentor Share
  studentMentors: { label: string; value: StudentMentorResponse }[] = [];
  selectedMentorToShare: { label: string; value: StudentMentorResponse } | null = null;
  isSharingWithMentor = false;

  // Saved Results
  calculationResult: RoiCalculationResponse | null = null;
  selectedUniversityName = '';

  // Getters for Variant A
  get maxTimelineYear(): number {
    const years = this.calculationResult?.returns?.breakEvenYears || 0;
    return Math.max(3, Math.ceil(years));
  }

  get timelineYears(): number[] {
    const max = this.maxTimelineYear;
    const arr = [];
    for (let i = 0; i <= max; i++) {
      arr.push(i);
    }
    return arr;
  }

  get breakEvenPercent(): number {
    const years = this.calculationResult?.returns?.breakEvenYears || 0;
    const max = this.maxTimelineYear;
    return max > 0 ? (years / max) * 100 : 0;
  }

  // Getters for Variant B
  get maxTimelineYearVariantB(): number {
    const years = this.calculationResult?.returnsVariantB?.breakEvenYears || 0;
    return Math.max(3, Math.ceil(years));
  }

  get timelineYearsVariantB(): number[] {
    const max = this.maxTimelineYearVariantB;
    const arr = [];
    for (let i = 0; i <= max; i++) {
      arr.push(i);
    }
    return arr;
  }

  get breakEvenPercentVariantB(): number {
    const years = this.calculationResult?.returnsVariantB?.breakEvenYears || 0;
    const max = this.maxTimelineYearVariantB;
    return max > 0 ? (years / max) * 100 : 0;
  }

  constructor(
    private fb: FormBuilder,
    private store: Store,
    private router: Router,
    private instituteService: InstituteService,
    private roiCalculatorService: RoiCalculatorService,
    private messageService: MessageService,
    private mentorService: MentorService
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    // 1. First, check localStorage directly for instant check
    this.checkLoginStatus();

    // 2. Subscribe to User State to detect changes
    this.store.select(UserSelectors.getUser).subscribe({
      next: (user) => {
        if (user && user.id) {
          this.isLoggedIn = true;
          this.loadStudentMentors();
        } else {
          // Fallback to localStorage check again in case store isn't populated
          this.checkLoginStatus();
        }
      },
      error: () => {
        this.checkLoginStatus();
      }
    });

    // 3. Setup form change listeners for Level/University cascading
    this.roiForm.get('level')?.valueChanges.subscribe(() => {
      this.roiForm.get('university')?.setValue(null, { emitEvent: false });
      this.handleLevelOrUniversityChange();
    });

    this.roiForm.get('university')?.valueChanges.subscribe(() => {
      this.handleLevelOrUniversityChange();
    });

    // Listen to school changes to populate courses
    this.roiForm.get('school')?.valueChanges.subscribe(schoolOpt => {
      const courseCtrl = this.roiForm.get('course');
      courseCtrl?.setValue(null);
      
      const schoolName = schoolOpt?.code;
      if (schoolName && this.allCourses.length > 0) {
        const filteredCourses = this.allCourses
          .filter(c => c.majorOrStream && c.majorOrStream.includes(schoolName) && c.courseName)
          .map(c => c.courseName as string);
        
        const uniqueCourses = Array.from(new Set(filteredCourses)).sort();
        this.coursesList = uniqueCourses.map(c => ({ name: c, code: c }));
      } else {
        this.coursesList = [];
      }
    });

    // 4. Restore cached calculation if user just signed up / logged in
    this.restoreCachedCalculation();
  }

  private checkLoginStatus(): void {
    try {
      const accountStr = localStorage.getItem('account');
      if (accountStr) {
        const account = JSON.parse(accountStr);
        if (account && account.token) {
          const isExpired = new Date(account.expiration) < new Date();
          this.isLoggedIn = !isExpired;
          return;
        }
      }
    } catch (e) {
      console.warn('Error reading account from localStorage:', e);
    }
    this.isLoggedIn = false;
  }

  private initForm(): void {
    this.roiForm = this.fb.group({
      planToStart: [null, Validators.required],
      level: [null, Validators.required],
      university: [null, Validators.required],
      school: [null],
      course: [null],
      studentStatus: ['International', Validators.required],
      livingSituation: [null, Validators.required],
      scholarshipAmount: [null, [Validators.min(0)]],
      loanRecommendation: [false]
    });
  }

  handleLevelOrUniversityChange(): void {
    const lvl = this.roiForm.get('level')?.value;
    const uni = this.roiForm.get('university')?.value;

    if (uni && uni.name) {
      const name = uni.name.toLowerCase();
      if (name.includes('london') || name.includes('oxford') || name.includes('cambridge') || name.includes('lse') || name.includes('manchester') || name.includes('edinburgh') || name.includes('uk') || name.includes('united kingdom') || name.includes('imperial')) {
        this.currencySymbol = '£';
      } else {
        this.currencySymbol = '$';
      }
    } else {
      this.currencySymbol = '$';
    }

    const schoolCtrl = this.roiForm.get('school');
    const courseCtrl = this.roiForm.get('course');

    if (lvl === 'PG') {
      schoolCtrl?.setValidators([Validators.required]);
      courseCtrl?.setValidators([Validators.required]);
    } else {
      schoolCtrl?.clearValidators();
      courseCtrl?.clearValidators();
    }
    schoolCtrl?.updateValueAndValidity({ emitEvent: false });
    courseCtrl?.updateValueAndValidity({ emitEvent: false });

    if (lvl === 'PG' && uni && uni.id) {
      this.isLoadingCourses = true;
      this.roiCalculatorService.getRoiPgCourses(uni.id).subscribe({
        next: (response) => {
          this.isLoadingCourses = false;
          if (response && response.result) {
            this.allCourses = response.result;
            
            // Extract unique MajorOrStream values as Schools
            const schools = Array.from(new Set(
              this.allCourses
                .flatMap(c => c.majorOrStream || [])
                .filter((s): s is string => !!s)
            )).sort();

            this.schoolsList = schools.map(s => ({ name: s, code: s }));
            
            // Clear current school and course selections
            schoolCtrl?.setValue(null);
            courseCtrl?.setValue(null);
            this.coursesList = [];
          }
        },
        error: () => {
          this.isLoadingCourses = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error Loading Courses',
            detail: 'Could not fetch schools and courses for this university.'
          });
        }
      });
    } else {
      this.allCourses = [];
      this.schoolsList = [];
      this.coursesList = [];
      schoolCtrl?.setValue(null, { emitEvent: false });
      courseCtrl?.setValue(null, { emitEvent: false });
    }
  }
blockIfLocked(event: any): void {
  if (!this.roiForm.get('level')?.value) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    event?.originalEvent?.preventDefault?.();
    event?.originalEvent?.stopPropagation?.();
    // force-close overlay + blur input if it managed to open/focus
    setTimeout(() => {
      const el = document.activeElement as HTMLElement;
      el?.blur?.();
    });
  }
}
  // Handle autocomplete matching
  searchUniversities(event: { query: string }): void {
    const queryStr = event && event.query ? event.query.trim() : '';
    const selectedLevel = this.roiForm.get('level')?.value || '';
    
    this.roiCalculatorService.searchRoiUniversities(queryStr, selectedLevel).subscribe({
      next: (response: any) => {
        const list = response && response.result ? response.result : (Array.isArray(response) ? response : []);
        this.universitySearchSuggestions = list.map((u: any) => ({
          id: u.id,
          name: u.name
        }));
      },
      error: () => {
        this.universitySearchSuggestions = [];
      }
    });
  }

  toggleScholarship(secured: boolean): void {
    this.hasScholarship = secured;
    if (!secured) {
      this.roiForm.patchValue({ scholarshipAmount: 0 });
    }
  }

  calculateRoi(): void {
    if (this.roiForm.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Form Invalid',
        detail: 'Please select a valid university and fill all program details.'
      });
      return;
    }

    const formValues = this.roiForm.value;
    const selectedUni = formValues.university;

    if (!selectedUni || !selectedUni.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Invalid University',
        detail: 'Please select a university from the suggestion list.'
      });
      return;
    }

    this.selectedUniversityName = selectedUni.name;
    this.isLoading = true;

    const request = {
      universityId: selectedUni.id,
      level: formValues.level,
      schoolName: formValues.level === 'PG' ? formValues.school?.code : undefined,
      courseName: formValues.level === 'PG' ? formValues.course?.code : undefined,
      programType: formValues.level === 'PG' ? formValues.course?.code : undefined,
      studentStatus: formValues.studentStatus,
      livingSituation: formValues.livingSituation,
      scholarshipAmount: Number(formValues.scholarshipAmount || 0),
      suggestLoan: formValues.loanRecommendation
    };

    // Store input values in session storage in case they sign in
    sessionStorage.setItem('roi_pending_calc', JSON.stringify({
      request,
      universityName: selectedUni.name,
      planToStart: formValues.planToStart,
      hasScholarship: this.hasScholarship,
      livingSituation: formValues.livingSituation,
      studentStatus: formValues.studentStatus,
      level: formValues.level,
      school: formValues.school,
      course: formValues.course
    }));

    const startTime = Date.now();
    this.roiCalculatorService.calculateRoi(request).subscribe({
      next: (response) => {
        const elapsedTime = Date.now() - startTime;
        const minimumDelay = 2500; // 2.5 seconds minimum delay
        const remainingDelay = Math.max(0, minimumDelay - elapsedTime);

        setTimeout(() => {
          this.isLoading = false;
          this.calculationResult = response;
          if (response.investment?.tuition?.local) {
            const firstChar = response.investment.tuition.local.trim().charAt(0);
            if (isNaN(Number(firstChar))) {
              this.currencySymbol = firstChar;
            }
          }
          this.activeView = 'dashboard';
          this.formDialogVisible = false;
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, remainingDelay);
      },
      error: (err) => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Calculation Failed',
          detail: err?.message || 'Failed to project returns. Please try again.'
        });
      }
    });
  }

  // Restore calculation inputs on landing if cached
  private restoreCachedCalculation(): void {
    const cachedStr = sessionStorage.getItem('roi_pending_calc');
    if (cachedStr) {
      try {
        const cached = JSON.parse(cachedStr);
        // Pre-fill forms
        this.hasScholarship = cached.hasScholarship;
        this.roiForm.patchValue({
          planToStart: cached.planToStart,
          level: cached.level,
          university: { id: cached.request.universityId, name: cached.universityName },
          studentStatus: cached.studentStatus,
          livingSituation: cached.livingSituation,
          scholarshipAmount: cached.request.scholarshipAmount
        });

        if (cached.level === 'PG' && cached.request.universityId) {
          this.isLoadingCourses = true;
          this.roiCalculatorService.getRoiPgCourses(cached.request.universityId).subscribe({
            next: (response) => {
              this.isLoadingCourses = false;
              if (response && response.result) {
                this.allCourses = response.result;
                const schools = Array.from(new Set(
                  this.allCourses
                    .flatMap(c => c.majorOrStream || [])
                    .filter((s): s is string => !!s)
                )).sort();
                this.schoolsList = schools.map(s => ({ name: s, code: s }));

                // Restore school selection
                this.roiForm.patchValue({ school: cached.school }, { emitEvent: false });

                // Filter and restore course selection list
                if (cached.school?.code) {
                  const filteredCourses = this.allCourses
                    .filter(c => c.majorOrStream && c.majorOrStream.includes(cached.school.code) && c.courseName)
                    .map(c => c.courseName as string);
                  const uniqueCourses = Array.from(new Set(filteredCourses)).sort();
                  this.coursesList = uniqueCourses.map(c => ({ name: c, code: c }));
                  
                  this.roiForm.patchValue({ course: cached.course }, { emitEvent: false });
                }

                // If they just logged in, auto-calculate to show full dashboard
                if (this.isLoggedIn) {
                  this.calculateRoi();
                  sessionStorage.removeItem('roi_pending_calc'); // Clear once restored
                }
              }
            },
            error: () => {
              this.isLoadingCourses = false;
            }
          });
        } else {
          // If they just logged in, auto-calculate to show full dashboard
          if (this.isLoggedIn) {
            this.calculateRoi();
            sessionStorage.removeItem('roi_pending_calc'); // Clear once restored
          }
        }
      } catch (e) {
        console.warn('Could not restore cached calculation:', e);
      }
    }
  }

  onSignIn(): void {
    this.router.navigate(['/signin'], { queryParams: { redirectUrl: '/portal/roi-calculator' } });
  }

  onSignUp(): void {
    this.router.navigate(['/signup'], { queryParams: { redirectUrl: '/portal/roi-calculator' } });
  }

  showSimplified(): void {
    if (!this.isLoggedIn) {
      this.onSignIn();
      return;
    }
    this.simplifiedVisible = true;
  }

  goBackToDashboard(): void {
    this.simplifiedVisible = false;
  }

  startFullForm(): void {
    const element = document.getElementById('roi-form-container');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  openFormDialog(): void {
    this.formDialogVisible = true;
  }

  resetCalculator(): void {
    this.activeView = 'landing';
    this.simplifiedVisible = false;
    this.roiForm.reset({
      planToStart: null,
      level: null,
      university: null,
      school: null,
      course: null,
      studentStatus: 'International',
      livingSituation: null,
      scholarshipAmount: null,
      loanRecommendation: false
    });
    this.hasScholarship = false;
    this.calculationResult = null;
    this.allCourses = [];
    this.schoolsList = [];
    this.coursesList = [];
    sessionStorage.removeItem('roi_pending_calc');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  goBackToForm(): void {
    this.activeView = 'landing';
    setTimeout(() => {
      this.startFullForm();
    }, 100);
  }

  // Dynamic Chart Helpers
  formatAxisValue(factor: number, isProfit = false): string {
    if (!this.calculationResult) return '';
    const totalInvestment = this.calculationResult.investment.totalInvestment.rawLocal || 120000;
    const value = totalInvestment * factor;
    return this.formatRawValue(value, isProfit);
  }

  formatRawValue(value: number, isProfit = false): string {
    const absVal = Math.abs(value);
    
    let formatted = '';
    if (absVal === 0) {
      return `${this.currencySymbol}0`;
    } else if (absVal >= 1000000) {
      const mVal = (absVal / 1000000).toFixed(1);
      formatted = `${mVal}M`;
    } else {
      const kVal = Math.round(absVal / 1000);
      formatted = `${kVal}k`;
    }
    
    const sign = value < 0 ? '-' : (value > 0 && isProfit ? '+' : '');
    return `${sign}${this.currencySymbol}${formatted}`;
  }

  getBreakEvenBounds(): { bottomVal: number, topVal: number } {
    if (!this.calculationResult) return { bottomVal: -120000, topVal: 180000 };
    const breakEven = this.calculationResult.returns.breakEvenYears || 0;
    const totalInvestment = this.calculationResult.investment.totalInvestment.rawLocal || 120000;
    
    const r = 0.05;
    let s0 = 0;
    if (breakEven > 0) {
      s0 = totalInvestment * r / (Math.pow(1 + r, breakEven) - 1);
    }
    
    const values = [];
    for (let t = 0; t <= 4; t++) {
      let val = -totalInvestment;
      if (t > 0 && breakEven > 0) {
        val = -totalInvestment + s0 * (Math.pow(1 + r, t) - 1) / r;
      }
      values.push(val);
    }
    
    const minVal = -totalInvestment;
    const maxVal = Math.max(...values);
    const topVal = Math.max(totalInvestment * 0.5, maxVal * 1.1);
    
    return { bottomVal: minVal, topVal };
  }

  getBreakEvenYLabels(): string[] {
    const { bottomVal, topVal } = this.getBreakEvenBounds();
    const range = topVal - bottomVal;
    const labels = [];
    for (let i = 5; i >= 0; i--) {
      const val = bottomVal + (i / 5) * range;
      labels.push(this.formatRawValue(val, val > 0));
    }
    return labels;
  }

  getBreakEvenPath(): string {
    if (!this.calculationResult) return '';
    const breakEven = this.calculationResult.returns.breakEvenYears || 0;
    const totalInvestment = this.calculationResult.investment.totalInvestment.rawLocal || 120000;
    
    const { bottomVal, topVal } = this.getBreakEvenBounds();
    const range = topVal - bottomVal;
    
    const points = [];
    const xCoords = [0, 162.5, 325, 487.5, 650];
    
    const r = 0.05;
    let s0 = 0;
    if (breakEven > 0) {
      s0 = totalInvestment * r / (Math.pow(1 + r, breakEven) - 1);
    }
    
    for (let t = 0; t <= 4; t++) {
      const x = xCoords[t];
      let val = -totalInvestment;
      if (t > 0 && breakEven > 0) {
        val = -totalInvestment + s0 * (Math.pow(1 + r, t) - 1) / r;
      }
      
      let y = 200 - ((val - bottomVal) / range) * 200;
      y = Math.max(0, Math.min(200, y));
      points.push(`${x} ${y.toFixed(1)}`);
    }
    
    return `M ${points[0]} L ${points.slice(1).join(' L ')}`;
  }

  getRoiBounds(): { topRoi: number, topSavings: number } {
    if (!this.calculationResult) return { topRoi: 750, topSavings: 900000 };
    const breakEven = this.calculationResult.returns.breakEvenYears || 0;
    const totalInvestment = this.calculationResult.investment.totalInvestment.rawLocal || 120000;
    
    const r = 0.05;
    let s0 = 0;
    if (breakEven > 0) {
      s0 = totalInvestment * r / (Math.pow(1 + r, breakEven) - 1);
    }
    
    const roiPercentages = [];
    const savingsValues = [];
    for (let t = 0; t <= 4; t++) {
      let roiPercent = 0;
      let cumulativeSavings = 0;
      if (t > 0 && breakEven > 0) {
        const yearVal = this.selectedRoiYears === 10 ? t * 2.5 : t;
        cumulativeSavings = s0 * (Math.pow(1 + r, yearVal) - 1) / r;
        roiPercent = (cumulativeSavings / totalInvestment) * 100;
      }
      roiPercentages.push(roiPercent);
      savingsValues.push(cumulativeSavings);
    }
    
    const maxRoi = Math.max(...roiPercentages);
    const maxSavings = Math.max(...savingsValues);
    
    const topRoi = Math.max(150, maxRoi * 1.1);
    const topSavings = Math.max(totalInvestment, maxSavings * 1.1);
    
    return { topRoi, topSavings };
  }

  getRoiLeftLabels(): string[] {
    const { topSavings } = this.getRoiBounds();
    const labels = [];
    for (let i = 5; i >= 0; i--) {
      const val = (i / 5) * topSavings;
      labels.push(this.formatRawValue(val, val > 0));
    }
    return labels;
  }

  getRoiRightLabels(): string[] {
    const { topRoi } = this.getRoiBounds();
    const labels = [];
    for (let i = 5; i >= 0; i--) {
      const val = Math.round((i / 5) * topRoi);
      labels.push(`${val}%`);
    }
    return labels;
  }

  getRoiPath(): string {
    if (!this.calculationResult) return '';
    const breakEven = this.calculationResult.returns.breakEvenYears || 0;
    const totalInvestment = this.calculationResult.investment.totalInvestment.rawLocal || 120000;
    
    const { topRoi } = this.getRoiBounds();
    
    const points = [];
    const xCoords = [0, 162.5, 325, 487.5, 650];
    
    const r = 0.05;
    let s0 = 0;
    if (breakEven > 0) {
      s0 = totalInvestment * r / (Math.pow(1 + r, breakEven) - 1);
    }
    
    for (let t = 0; t <= 4; t++) {
      const x = xCoords[t];
      let roiPercent = 0;
      if (t > 0 && breakEven > 0) {
        const yearVal = this.selectedRoiYears === 10 ? t * 2.5 : t;
        const cumulativeSavings = s0 * (Math.pow(1 + r, yearVal) - 1) / r;
        roiPercent = (cumulativeSavings / totalInvestment) * 100;
      }
      
      let y = 200 - (roiPercent / topRoi) * 200;
      y = Math.max(0, Math.min(200, y));
      points.push(`${x} ${y.toFixed(1)}`);
    }
    
    return `M ${points[0]} L ${points.slice(1).join(' L ')}`;
  }

  // Actions for dashboard footer
  saveResults(): void {
    if (!this.isLoggedIn) {
      this.onSignIn();
      return;
    }
    this.messageService.add({
      severity: 'success',
      summary: 'Saved',
      detail: 'Your ROI projection has been saved to your profile.'
    });
  }

  downloadPdf(): void {
    if (!this.isLoggedIn) {
      this.onSignIn();
      return;
    }
    window.print();
  }

  share(): void {
    if (!this.isLoggedIn) {
      this.onSignIn();
      return;
    }
    if (navigator.share) {
      navigator.share({
        title: 'My GradPilots ROI Projections',
        text: `Check out my ROI analysis for ${this.selectedUniversityName}!`,
        url: window.location.href
      }).catch(() => {});
    } else {
      // Fallback: Copy URL to clipboard
      navigator.clipboard.writeText(window.location.href);
      this.messageService.add({
        severity: 'info',
        summary: 'Copied',
        detail: 'Shareable link copied to clipboard!'
      });
    }
  }

  getRecommendationPointerPosition(): string {
    const recommendation = this.calculationResult?.recommendation;
    const tier = recommendation?.tier || '';
    switch (tier.toLowerCase()) {
      case 'borrow carefully':
        return '10%'; // Or '30%' depending on how bad it is, defaulting to 10% for negative ROI
      case 'loan is good':
        return '50%';
      case 'loan is great':
        return '70%';
      case 'amazing investment':
        return '90%';
      default:
        return '90%';
    }
  }

  navigateToScholarships(): void {
    const targetCountry = this.roiForm.get('targetCountry')?.value;
    const targetLevel = this.roiForm.get('level')?.value;
    const queryParams: any = { tab: 'scholarships' };
    
    if (targetCountry) {
      queryParams.country = targetCountry;
    }

    if (targetLevel) {
      queryParams.level = targetLevel;
    }

    if (this.selectedUniversityName) {
      queryParams.search = this.selectedUniversityName;
    }

    this.router.navigate(['/portal/institute-search'], { queryParams });
  }

  // ──────────────────────────────────────────
  // Mentor Share
  // ──────────────────────────────────────────
  loadStudentMentors(): void {
    this.mentorService.getStudentMentors(MentorType.Mentor).subscribe({
      next: (response) => {
        const mentors = response?.result ?? [];
        this.studentMentors = mentors.map((m: StudentMentorResponse) => ({
          label: `${m.firstName} ${m.lastName}`.trim(),
          value: m
        }));
      },
      error: () => {
        this.studentMentors = [];
      }
    });
  }

  shareRoiWithMentor(): void {
    if (!this.selectedMentorToShare) return;
    this.isSharingWithMentor = true;
    setTimeout(() => {
      this.messageService.add({
        severity: 'success',
        summary: 'Shared!',
        detail: `ROI results shared with ${this.selectedMentorToShare!.label}`
      });
      this.isSharingWithMentor = false;
      this.selectedMentorToShare = null;
    }, 800);
  }

  navigateToMentors(): void {
    this.router.navigate(['/portal/mentor-me']);
  }
}

